import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MonthlyTransfusionTrends from '../../components/charts/MonthlyTransfusionTrends';
import MonthlyDiscardTrends from '../../components/charts/MonthlyDiscardTrends';
import MonthlyTransferredTrends from '../../components/charts/MonthlyTransferredTrends';
import MonthlyRedistributedTrends from '../../components/charts/MonthlyRedistributedTrends';
import { chartApi } from '../../lib/api-client';
import { usePageTitle } from '../../hooks/usePageTitle';

type DateRange = {
  startDate?: string;
  endDate?: string;
  year?: number;
};

function getFiscalYearRange(fiscalYearStart: number): DateRange {
  return {
    startDate: `${fiscalYearStart}-04-01`,
    endDate: `${fiscalYearStart + 1}-03-31`,
  };
}

/** Most recently completed Ontario fiscal year (Apr–Mar). */
function getLastFiscalYearStart(now = new Date()): number {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11
  return currentMonth < 3 ? currentYear - 2 : currentYear - 1;
}

function isFiscalYearRange(range: DateRange): boolean {
  return Boolean(
    range.startDate?.endsWith('-04-01') && range.endDate?.endsWith('-03-31')
  );
}

export default function Utilization() {
  usePageTitle('Utilization');
  const lastFyStart = getLastFiscalYearStart();
  const [dateRange, setDateRange] = useState<DateRange>(getFiscalYearRange(lastFyStart));
  const [rangeNote, setRangeNote] = useState<string | null>(null);
  const [resolvingRange, setResolvingRange] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function resolveDefaultRange() {
      const preferred = getFiscalYearRange(lastFyStart);
      try {
        const response = await chartApi.getTemporalData({
          startDate: preferred.startDate,
          endDate: preferred.endDate,
        });
        if (cancelled) return;

        if (response.success && (!response.data || response.data.length === 0)) {
          const fallback = getFiscalYearRange(lastFyStart - 1);
          setDateRange(fallback);
          setRangeNote(
            `No chart data for FY ${lastFyStart}/${String(lastFyStart + 1).slice(2)} yet — showing FY ${lastFyStart - 1}/${String(lastFyStart).slice(2)}.`
          );
        } else {
          setDateRange(preferred);
          setRangeNote(null);
        }
      } catch {
        if (!cancelled) {
          setDateRange(preferred);
          setRangeNote(null);
        }
      } finally {
        if (!cancelled) setResolvingRange(false);
      }
    }

    resolveDefaultRange();
    return () => {
      cancelled = true;
    };
  }, [lastFyStart]);

  const handleDateRangeChange = (
    type: 'last-fiscal-year' | 'previous-fiscal-year' | 'custom' | 'year',
    year?: number,
    startDate?: string,
    endDate?: string
  ) => {
    setRangeNote(null);
    if (type === 'last-fiscal-year') {
      setDateRange(getFiscalYearRange(getLastFiscalYearStart()));
    } else if (type === 'previous-fiscal-year') {
      setDateRange(getFiscalYearRange(getLastFiscalYearStart() - 1));
    } else if (type === 'custom' && startDate && endDate) {
      setDateRange({ startDate, endDate });
    } else if (type === 'year' && year) {
      setDateRange({
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
      });
    }
  };

  const lastFy = getFiscalYearRange(getLastFiscalYearStart());
  const prevFy = getFiscalYearRange(getLastFiscalYearStart() - 1);
  const isLastFy =
    dateRange.startDate === lastFy.startDate && dateRange.endDate === lastFy.endDate;
  const isPrevFy =
    dateRange.startDate === prevFy.startDate && dateRange.endDate === prevFy.endDate;
  const isCalendarYear =
    Boolean(dateRange.startDate?.endsWith('-01-01') && dateRange.endDate?.endsWith('-12-31')) &&
    !isFiscalYearRange(dateRange);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to="/data" className="text-sm font-medium text-primary hover:text-primary-dark">
          Projects
        </Link>
        <h1 className="mt-2 text-3xl font-bold text-gray-900">Utilization</h1>
        <p className="mt-2 text-gray-600">
          Monthly trends for transfusions, discards, transfers, and redistributions
        </p>
      </div>

      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Date Range:</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleDateRangeChange('last-fiscal-year')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isLastFy
                  ? 'bg-[#00467f] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last Fiscal Year
            </button>
            <button
              type="button"
              onClick={() => handleDateRangeChange('previous-fiscal-year')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isPrevFy
                  ? 'bg-[#00467f] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Prior Fiscal Year
            </button>
            <button
              type="button"
              onClick={() => {
                const currentYear = new Date().getFullYear();
                handleDateRangeChange('year', currentYear);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isCalendarYear
                  ? 'bg-[#00467f] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Current Year
            </button>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateRange.startDate || ''}
                onChange={(e) => {
                  if (e.target.value && dateRange.endDate) {
                    handleDateRangeChange('custom', undefined, e.target.value, dateRange.endDate);
                  }
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00467f] focus:border-[#00467f]"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.endDate || ''}
                onChange={(e) => {
                  if (e.target.value && dateRange.startDate) {
                    handleDateRangeChange('custom', undefined, dateRange.startDate, e.target.value);
                  }
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00467f] focus:border-[#00467f]"
              />
            </div>
          </div>
        </div>
        {rangeNote && (
          <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            {rangeNote}
          </p>
        )}
      </div>

      {resolvingRange ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <div className="text-gray-500">Loading chart data...</div>
        </div>
      ) : (
        <div className="space-y-6">
          <MonthlyTransfusionTrends
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            year={dateRange.year}
          />
          <MonthlyDiscardTrends
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            year={dateRange.year}
          />
          <MonthlyTransferredTrends
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            year={dateRange.year}
          />
          <MonthlyRedistributedTrends
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            year={dateRange.year}
          />
        </div>
      )}
    </div>
  );
}
